import { HeatMechanicalResult } from "../../models/HeatMechanicalResult";
import { MechanicalTestRunDisposition } from "../../models/MechanicalTestRunDisposition";
import { getSampleStatusLabel } from "./format";
import { VALUE_COLUMNS } from "./layout";
import { MechanicalResultValues } from "./types";

export type RunGroup = {
	runId: number;
	reception: Date | null;
	reported: Date | null;
	results: HeatMechanicalResult[];
	dispositionHistory: MechanicalTestRunDisposition[];
	disposition: MechanicalTestRunDisposition | null;
	comment: string | null;
};

export type RunSnapshot = {
	at: Date;
	source: "auto" | "manual";
	kinds: ("results" | "disposition")[];
	updatedFields: Map<string, string | null>;
	group: RunGroup;
};

export const valueChangeKey = (
	mechanicalSampleId: number,
	field: keyof MechanicalResultValues,
) => `${mechanicalSampleId}:${field}`;

export const overallChangeKey = (mechanicalSampleId: number) =>
	`${mechanicalSampleId}:overall`;

export const PLACEHOLDER_RUN_ID = 0;

const time = (value: Date | string) => new Date(value).getTime();

const byDisposedAtDesc = (
	a: MechanicalTestRunDisposition,
	b: MechanicalTestRunDisposition,
) => time(b.DisposedAt) - time(a.DisposedAt);

const byReceptionDesc = (a: RunGroup, b: RunGroup) => {
	if (!a.reception) return 1;
	if (!b.reception) return -1;
	return time(b.reception) - time(a.reception);
};

const derived = (
	results: HeatMechanicalResult[],
	disposition: MechanicalTestRunDisposition | null,
) => ({
	results,
	disposition,
	comment: disposition?.Comment ?? null,
});

export const buildRunGroups = (
	results: HeatMechanicalResult[],
	dispositions: MechanicalTestRunDisposition[],
	allowEmptyRun = false,
): RunGroup[] => {
	const byRun = new Map<number, HeatMechanicalResult[]>();

	for (const result of results) {
		const group = byRun.get(result.MechanicalTestRunId);
		if (group) group.push(result);
		else byRun.set(result.MechanicalTestRunId, [result]);
	}

	for (const disposition of dispositions) {
		if (!byRun.has(disposition.MechanicalTestRunId))
			byRun.set(disposition.MechanicalTestRunId, []);
	}

	if (byRun.size === 0 && allowEmptyRun)
		return [
			{
				runId: PLACEHOLDER_RUN_ID,
				reception: null,
				reported: null,
				dispositionHistory: [],
				...derived([], null),
			},
		];

	return Array.from(byRun.entries())
		.map(([runId, rows]) => {
			const dispositionHistory = dispositions
				.filter((d) => d.MechanicalTestRunId === runId && d.IsActive !== false)
				.sort(byDisposedAtDesc);
			const disposition = dispositionHistory[0] ?? null;

			return {
				runId,
				reception: rows[0]?.Reception ?? disposition?.Reception ?? null,
				reported: rows[0]?.Reported ?? disposition?.Reported ?? null,
				dispositionHistory,
				...derived(rows, disposition),
			};
		})
		.sort(byReceptionDesc);
};

const isLater = (a: HeatMechanicalResult, b: HeatMechanicalResult) =>
	time(a.UpdatedAt) > time(b.UpdatedAt) ||
	(time(a.UpdatedAt) === time(b.UpdatedAt) &&
		a.HeatMechanicalResultId > b.HeatMechanicalResultId);

const dispositionSource = (disposition: MechanicalTestRunDisposition) =>
	disposition.DisposedBy?.trim().toLowerCase() === "auto" ? "auto" : "manual";

const resultsSource = (at: number, versions: HeatMechanicalResult[]) =>
	versions.some((version) => time(version.UpdatedAt) === at && version.IsManual)
		? "manual"
		: "auto";

type RunMoment = {
	at: number;
	source: "auto" | "manual";
	resultsAt: number | null;
	disposition: MechanicalTestRunDisposition | null;
};

const buildMoments = (
	versions: HeatMechanicalResult[],
	dispositions: MechanicalTestRunDisposition[],
): RunMoment[] => {
	const resultTimes = Array.from(
		new Set(versions.map((version) => time(version.UpdatedAt))),
	).sort((a, b) => a - b);

	const merged = new Set<number>();
	const moments: RunMoment[] = [];

	for (const disposition of [...dispositions].sort(
		(a, b) => time(a.DisposedAt) - time(b.DisposedAt),
	)) {
		const at = time(disposition.DisposedAt);
		const source = dispositionSource(disposition);

		const [resultsAt] = resultTimes
			.filter(
				(candidate) =>
					!merged.has(candidate) &&
					Math.abs(candidate - at) <= 60_000 &&
					resultsSource(candidate, versions) === source,
			)
			.sort((a, b) => Math.abs(a - at) - Math.abs(b - at));

		if (resultsAt !== undefined) merged.add(resultsAt);

		moments.push({
			at: Math.max(at, resultsAt ?? at),
			source,
			resultsAt: resultsAt ?? null,
			disposition,
		});
	}

	for (const at of resultTimes)
		if (!merged.has(at))
			moments.push({
				at,
				source: resultsSource(at, versions),
				resultsAt: at,
				disposition: null,
			});

	return moments.sort((a, b) => a.at - b.at);
};

const numberLabel = (value: number | null) =>
	value == null ? null : String(value);

const passedLabel = (passed: boolean | null) =>
	passed == null ? null : passed ? "Pass" : "Fail";

const updatedFieldsAt = (
	moment: RunMoment,
	versions: HeatMechanicalResult[],
	dispositions: MechanicalTestRunDisposition[],
) => {
	const updated = new Map<string, string | null>();

	if (moment.resultsAt !== null)
		for (const result of versions) {
			if (time(result.UpdatedAt) !== moment.resultsAt) continue;

			const previous = versions.reduce<HeatMechanicalResult | null>(
				(held, version) =>
					version.MechanicalSampleId === result.MechanicalSampleId &&
					isLater(result, version) &&
					(!held || isLater(version, held))
						? version
						: held,
				null,
			);

			for (const column of VALUE_COLUMNS) {
				const before = previous ? column.value(previous) : null;

				if (before !== column.value(result))
					updated.set(
						valueChangeKey(result.MechanicalSampleId, column.field),
						numberLabel(before),
					);
			}

			const beforePassed = previous?.OverallPassed ?? null;

			if (beforePassed !== result.OverallPassed)
				updated.set(
					overallChangeKey(result.MechanicalSampleId),
					passedLabel(beforePassed),
				);
		}

	const disposition = moment.disposition;

	if (disposition) {
		const previous =
			dispositions.find(
				(candidate) =>
					time(candidate.DisposedAt) < time(disposition.DisposedAt),
			) ?? null;

		if (previous?.Disposition !== disposition.Disposition)
			updated.set(
				"disposition",
				previous ? getSampleStatusLabel(previous.Disposition).label : null,
			);
	}

	return updated;
};

export const buildRunSnapshots = (
	group: RunGroup,
	versions: HeatMechanicalResult[],
): RunSnapshot[] => {
	const dispositions = group.dispositionHistory;

	return buildMoments(versions, dispositions).map((m) => {
		const bySpecimen = new Map<number, HeatMechanicalResult>();

		for (const version of versions) {
			if (time(version.UpdatedAt) > m.at) continue;

			const held = bySpecimen.get(version.MechanicalSampleId);
			if (!held || isLater(version, held))
				bySpecimen.set(version.MechanicalSampleId, version);
		}

		const results = Array.from(bySpecimen.values()).sort(
			(a, b) => a.SampleSequence - b.SampleSequence,
		);

		const disposition =
			dispositions.find((candidate) => time(candidate.DisposedAt) <= m.at) ??
			null;

		const kinds: RunSnapshot["kinds"] = [];
		if (m.resultsAt !== null) kinds.push("results");
		if (m.disposition) kinds.push("disposition");

		return {
			at: new Date(m.at),
			source: m.source,
			kinds,
			updatedFields: updatedFieldsAt(m, versions, dispositions),
			group: { ...group, ...derived(results, disposition) },
		};
	});
};
